<template>
  <div>
    <Nav></Nav>
    <b-container fluid>
      <b-row class="mt-3">
        <b-col>
          <h1>Tasa en línea, {{ granularityHeading }}</h1>
          <p class="text-muted mb-0" v-if="sucursalName">
            Sucursal: <strong>{{ sucursalName }}</strong>
          </p>
        </b-col>
        <b-col md="auto" class="d-flex align-items-end">
          <base-button
            type="primary"
            size="sm"
            @click="$router.push('/sucursal/' + sucursalId)"
          >Volver a sucursal</base-button>
        </b-col>
      </b-row>

      <b-card class="mt-3" no-body body-class="p-3">
        <b-row class="align-items-end">
          <b-col md="3" sm="6" cols="12">
            <b-form-group label="Desde">
              <b-form-input type="date" v-model="fromDate" :max="toDate"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="3" sm="6" cols="12">
            <b-form-group label="Hasta">
              <b-form-input type="date" v-model="toDate" :min="fromDate"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="2" sm="6" cols="12">
            <b-form-group label="Granularidad">
              <b-form-select v-model="granularityMode" :options="granularityOptions"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="2" sm="6" cols="12">
            <b-form-group label="Zona horaria">
              <b-form-input v-model="timezone" placeholder="UTC, America/Mexico_City, ..."></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="2" sm="6" cols="12">
            <b-form-group label="&nbsp;">
              <base-button type="primary" block @click="loadData" :disabled="loading">
                <span v-if="loading">Cargando…</span>
                <span v-else>Actualizar</span>
              </base-button>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row v-if="granularityMode === 'auto'">
          <b-col>
            <small class="text-muted">
              Granularidad automática: <strong>{{ granularityHeading }}</strong>
              ({{ dayCount }} día{{ dayCount === 1 ? '' : 's' }} en el rango)
            </small>
          </b-col>
        </b-row>
      </b-card>

      <b-card class="mt-3" no-body body-class="p-3">
        <b-row>
          <b-col>
            <h3 class="mb-3">Tasa en línea</h3>
          </b-col>
        </b-row>

        <div v-if="loading" class="text-center py-5">
          <b-spinner></b-spinner>
        </div>

        <div v-else-if="!grid.cells.length" class="text-center text-muted py-5">
          Sin datos en el rango seleccionado.
        </div>

        <div v-else class="online-rate-grid-wrapper">
          <table class="online-rate-grid" :class="'granularity-' + grid.granularity">
            <thead>
              <tr>
                <th class="axis-label"></th>
                <th v-for="(label, i) in grid.x_labels" :key="'x' + i">{{ label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(yLabel, yi) in grid.y_labels" :key="'y' + yi">
                <td class="axis-label">{{ yLabel }}</td>
                <td
                  v-for="(xLabel, xi) in grid.x_labels"
                  :key="xi"
                  class="rate-cell"
                  :title="cellTooltip(grid.y_keys[yi], grid.x_keys[xi])"
                >
                  <div class="rate-bar" :style="cellStyle(grid.y_keys[yi], grid.x_keys[xi])"></div>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="online-rate-legend mt-3">
            <div class="legend-item" v-for="(stop, idx) in legendStops" :key="idx">
              <span class="legend-swatch" :style="{ background: stop.color }"></span>
              <span class="legend-label">{{ stop.label }}</span>
            </div>
            <div class="legend-item">
              <span class="legend-swatch" style="background: #eaeaea"></span>
              <span class="legend-label">Sin datos</span>
            </div>
          </div>
        </div>
      </b-card>

      <b-card class="mt-3" no-body body-class="p-3">
        <b-row>
          <b-col>
            <h3 class="mb-3">Eventos recientes</h3>
          </b-col>
          <b-col md="auto">
            <base-button type="primary" size="sm" @click="loadEvents" :disabled="loadingEvents">
              <b-icon-arrow-clockwise></b-icon-arrow-clockwise>
            </base-button>
          </b-col>
        </b-row>
        <b-table
          small
          responsive
          striped
          hover
          :items="events"
          :fields="eventFields"
          :busy="loadingEvents"
          empty-text="Sin eventos en el rango seleccionado"
          show-empty
        >
          <template #cell(status)="row">
            <b-badge v-if="row.item.status" variant="success">En línea</b-badge>
            <b-badge v-else variant="danger">Fuera de línea</b-badge>
          </template>
          <template #cell(source)="row">
            <span v-if="row.item.source === 'lwt'" class="text-danger">LWT</span>
            <span v-else-if="row.item.source === 'explicit'" class="text-success">Pos local</span>
            <span v-else>{{ row.item.source }}</span>
          </template>
        </b-table>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import Nav from "../components/Nav.vue";
import axios from "axios";
import moment from "moment";

export default {
  name: "SucursalOnlineRate",
  components: { Nav },
  data() {
    return {
      sucursalId: null,
      sucursalName: "",
      timezone: "UTC",
      fromDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      loading: false,
      loadingEvents: false,
      // granularityMode is what the user picks ("auto" by default). The
      // backend always echoes the resolved granularity back in grid.granularity,
      // which is what the rendering code keys off.
      granularityMode: "auto",
      granularityOptions: [
        { value: "auto", text: "Automática" },
        { value: "hour", text: "Por hora (semana)" },
        { value: "weekly", text: "Por semana" },
        { value: "monthly", text: "Por mes" },
      ],
      grid: {
        granularity: "hour",
        x_labels: [],
        y_labels: [],
        x_keys: [],
        y_keys: [],
        cells: [],
      },
      events: [],
      eventFields: [
        { key: "changed_at", label: "Fecha/hora" },
        { key: "status", label: "Estado" },
        { key: "source", label: "Origen" },
      ],
      legendStops: [
        { label: "0 %", color: "#d9534f" },
        { label: "20 %", color: "#e9836b" },
        { label: "40 %", color: "#f0a3a3" },
        { label: "60 %", color: "#f8d6a3" },
        { label: "80 %", color: "#f9e79f" },
        { label: "100 %", color: "#8fd5a0" },
      ],
    };
  },
  computed: {
    dayCount() {
      return moment(this.toDate).diff(moment(this.fromDate), "days") + 1;
    },
    // Mirror the backend's auto-selection rule so the UI hint stays in sync
    // with the actual granularity that will be returned.
    autoGranularity() {
      const n = this.dayCount;
      if (n > 90) return "monthly";
      if (n > 30) return "weekly";
      return "hour";
    },
    resolvedGranularity() {
      return this.granularityMode === "auto" ? this.autoGranularity : this.granularityMode;
    },
    granularityHeading() {
      switch (this.grid.granularity) {
        case "monthly":
          return "por día del mes";
        case "weekly":
          return "por semana";
        default:
          return "por hora";
      }
    },
    cellMap() {
      const m = {};
      for (const c of this.grid.cells) {
        m[c.y_key + "|" + c.x_key] = c;
      }
      return m;
    },
  },
  methods: {
    cellStyle(yKey, xKey) {
      const c = this.cellMap[yKey + "|" + xKey];
      if (!c || c.total_seconds <= 0) {
        return { background: "#eaeaea" };
      }
      return { background: this.colorForRate(c.online_rate) };
    },
    cellTooltip(yKey, xKey) {
      const c = this.cellMap[yKey + "|" + xKey];
      if (!c) return "";
      const heading = `${c.y_label} · ${c.x_label}`;
      if (c.total_seconds <= 0) {
        return `${heading} — sin datos`;
      }
      return `${heading} — ${c.online_rate.toFixed(1)}% en línea`;
    },
    colorForRate(rate) {
      const clamped = Math.max(0, Math.min(100, rate));
      const colors = [
        [217, 83, 79],
        [233, 131, 107],
        [240, 163, 163],
        [248, 214, 163],
        [249, 231, 159],
        [143, 213, 160],
      ];
      const step = 100 / (colors.length - 1);
      const idx = Math.min(colors.length - 2, Math.floor(clamped / step));
      const t = (clamped - idx * step) / step;
      const a = colors[idx];
      const b = colors[idx + 1];
      const mix = a.map((c, i) => Math.round(c + (b[i] - c) * t));
      return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`;
    },
    async loadData() {
      this.loading = true;
      try {
        const params = {
          from: this.fromDate,
          to: this.toDate,
          tz: this.timezone || "UTC",
          granularity: this.resolvedGranularity,
        };
        const res = await axios.get(
          "/sucursal/" + this.sucursalId + "/connection/online-rate",
          { params }
        );
        // Backend returns a `grid` object. Keep the local one structured the
        // same so the template never has to null-check axes.
        const g = res.data.grid || {};
        this.grid = {
          granularity: g.granularity || params.granularity,
          x_labels: g.x_labels || [],
          y_labels: g.y_labels || [],
          x_keys: g.x_keys || [],
          y_keys: g.y_keys || [],
          cells: g.cells || [],
        };
      } catch (err) {
        this.$bvToast.toast(
          err.response?.data || err.message || "Error al cargar tasa en línea",
          { title: "Error", variant: "danger" }
        );
      } finally {
        this.loading = false;
      }
      this.loadEvents();
    },
    async loadEvents() {
      this.loadingEvents = true;
      try {
        const params = {
          from: this.fromDate,
          to: this.toDate,
          tz: this.timezone || "UTC",
          limit: 200,
        };
        const res = await axios.get(
          "/sucursal/" + this.sucursalId + "/connection/log",
          { params }
        );
        this.events = res.data.events || [];
      } catch (err) {
        this.$bvToast.toast(
          err.response?.data || err.message || "Error al cargar eventos",
          { title: "Error", variant: "danger" }
        );
      } finally {
        this.loadingEvents = false;
      }
    },
    async loadSucursal() {
      try {
        const res = await axios.get("/sucursal/" + this.sucursalId);
        this.sucursalName = res.data?.nombre || "";
      } catch (err) {
        // Non-blocking: the heatmap still works without the name.
      }
    },
  },
  mounted() {
    this.sucursalId = this.$route.params.id;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) this.timezone = tz;
    } catch (e) {
      /* keep UTC */
    }
    this.loadSucursal();
    this.loadData();
  },
};
</script>

<style scoped>
.online-rate-grid-wrapper {
  overflow-x: auto;
}
.online-rate-grid {
  border-collapse: separate;
  border-spacing: 4px 6px;
  table-layout: fixed;
}
/* Hour mode is the original 24-row, 7-col layout. Let it stretch full
   width so cells stay generous on wide screens. */
.online-rate-grid.granularity-hour {
  width: 100%;
}
/* Weekly/monthly axes have many columns — give each a comfortable fixed
   width and let the wrapper scroll horizontally instead of squashing
   labels into one another. */
.online-rate-grid.granularity-weekly,
.online-rate-grid.granularity-monthly {
  min-width: 100%;
}
.online-rate-grid.granularity-weekly td.rate-cell,
.online-rate-grid.granularity-weekly th:not(.axis-label),
.online-rate-grid.granularity-monthly td.rate-cell,
.online-rate-grid.granularity-monthly th:not(.axis-label) {
  min-width: 60px;
}
.online-rate-grid th {
  font-weight: 600;
  font-size: 0.85rem;
  text-align: center;
  color: #525f7f;
}
.online-rate-grid td.axis-label,
.online-rate-grid th.axis-label {
  width: 80px;
  text-align: right;
  padding-right: 10px;
  font-size: 0.8rem;
  color: #8898aa;
  white-space: nowrap;
}
.rate-cell {
  padding: 0;
}
.rate-bar {
  height: 14px;
  border-radius: 8px;
  width: 100%;
}
.online-rate-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
}
.legend-swatch {
  display: inline-block;
  width: 28px;
  height: 14px;
  border-radius: 4px;
  margin-right: 6px;
  vertical-align: middle;
}
.legend-label {
  font-size: 0.85rem;
  color: #525f7f;
}
</style>
